import { useState } from "react";
import { FaApple } from "react-icons/fa";
import { FcGoogle } from "react-icons/fc";
import "react-phone-input-2/lib/style.css";

// Assets and Custom Components
import logo from '../../../assets/png/logoTransperent.png';
import TextFieldCustom from "../../../component/elements/textfieldCustom";
import CustomButton from "../../../component/elements/CustomButton/CustomButton";
import { useSnackbar } from "../../../component/SnackbarContext";

export default function Email({ onContinue, role }) {
    // Logic kept for future use, but UI currently locked to "email"
    const [mode, setMode] = useState("email"); 
    const [email, setEmail] = useState("");
    const [phone, setPhone] = useState("");
    const [error, setError] = useState("");
    const showSnackbar = useSnackbar();

    const handleChange = (e) => {
        const value = e.target.value;
        if (value.startsWith(" ") && value.length === 1) return;
        setEmail(value);
        if (error) setError("");
    };

    const handleContinue = () => {
        if (mode === "email") {
            const trimmedEmail = email.trim();
            if (!trimmedEmail) {
                setError("Email is required");
            } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedEmail)) {
                setError("Please enter a valid email");
            } else {
                setError("");
                onContinue({ type: "email", value: trimmedEmail });
            }
        } else {
            // Logic preserved for future phone login
            if (!phone) {
                setError("Phone number is required");
            } else if (phone.replace(/\D/g, "").length < 10) {
                setError("Please enter a valid phone number");
            } else {
                setError("");
                onContinue({ type: "phone", value: `+${phone}` });
            }
        }
    };

    const handleSocialAuth = (provider) => {
        const selectedRole = role || "normal user";
        
        window.location.href = `https://tydii.io/api/auth/${provider}/init?userType=${encodeURIComponent(selectedRole)}`;
    }

    return (
        <div className="w-full max-w-[440px] mx-auto bg-white rounded-[32px] shadow-[0_20px_50px_rgba(0,0,0,0.1)] px-10 py-12 border border-gray-50">
            {/* Header Section */}
            <div className="flex flex-col items-center mb-10 text-center">
                <img
                    src={logo}
                    alt="TYDII Logo"
                    className="h-14 w-auto mb-6 transform hover:scale-105 transition-transform duration-300"
                />
                <h2 className="text-3xl font-bold text-gray-900 tracking-tight">Sign in</h2>
                <p className="text-gray-500 mt-2 font-medium">
                    Enter your email to access your account
                </p>
            </div>

            {/* Input Section - Phone logic preserved but UI removed */}
            <div className="space-y-6">
                <div className="relative">
                    <label className="block text-sm font-semibold text-gray-700 mb-2 ml-1">
                        Email Address
                    </label>
                    
                    <TextFieldCustom
                        placeholder="e.g. alex@example.com"
                        fullWidth
                        value={email}
                        onChange={handleChange}
                        error={Boolean(error)}
                        helperText={error}
                        className="rounded-2xl transition-all duration-200"
                    />
                </div>

                <CustomButton
                    onClick={handleContinue}
                    customeClass="w-full h-[56px] !rounded-2xl text-lg font-bold shadow-lg shadow-blue-900/10 hover:shadow-blue-900/20 active:scale-[0.98] transition-all"
                >
                    Continue
                </CustomButton>
            </div>

            {/* Divider */}
            <div className="relative flex items-center py-10">
                <div className="flex-grow border-t border-gray-100 "></div>
                <span className="flex-shrink mx-4 text-gray-400 text-xs font-bold uppercase tracking-[0.15em]">
                    Or continue with
                </span>
                <div className="flex-grow border-t border-gray-100"></div>
            </div>

            {/* Social Grid - Phone option removed, layout adjusted to 2 columns */}
            <div className="grid grid-cols-2 gap-4">
                {/* Google Login */}
                <button
                    onClick={() => handleSocialAuth('google')}
                    className="flex flex-col items-center justify-center h-20 rounded-2xl border-2 border-gray-50 bg-white hover:bg-gray-50 hover:border-gray-200 hover:shadow-sm transition-all group"
                >
                    <div className="group-hover:scale-110 transition-transform">
                        <FcGoogle size={28} />
                    </div>
                    <span className="text-[10px] mt-2 font-bold text-gray-500 uppercase tracking-tight">Google</span>
                </button>

                {/* Apple Login */}
                <button
                    onClick={() => handleSocialAuth('apple')}
                    className="flex flex-col items-center justify-center h-20 rounded-2xl border-2 border-gray-50 bg-white hover:bg-gray-50 hover:border-gray-200 hover:shadow-sm transition-all group"
                >
                    <div className="p-2 rounded-full bg-black text-white group-hover:scale-110 transition-transform">
                        <FaApple size={20} />
                    </div>
                    <span className="text-[10px] mt-2 font-bold text-gray-500 uppercase tracking-tight">Apple</span>
                </button>
            </div>
        </div>
    );
}
